"use client";
import { useRef, useEffect, forwardRef, HTMLProps, ChangeEvent, FocusEvent } from "react";
import { useSearchParams } from "next/navigation";
import { useSearchStore } from "@/store/search";
import { getSearchResults } from "@/utils/search";
import { SEARCH_PARAM_KEY, SEARCH_MIN_QUERY } from "@/utils/constants";
import { getLang } from "@/utils/selectors";
import { SvgIconSearch } from "@/components/common/Svg";
import Field from "@/components/common/Field";
import styles from "../Search.module.css";

export interface SearchFormProps extends HTMLProps<HTMLFormElement | HTMLInputElement> {
	onInputFocus?: () => void;
	onInputBlur?: () => void;
	onShowResults: (showResults: boolean) => void;
}

const SearchForm = forwardRef<HTMLFormElement, SearchFormProps>(({
	onInputFocus,
	onInputBlur,
	onShowResults
}: SearchFormProps, ref) => {
	const searchParams = useSearchParams();
	const fieldRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
	const searchQuery = useSearchStore(store => store.searchQuery);
	// const isSearching = useSearchStore(store => store.isSearching);
	const setIsSearching = useSearchStore(store => store.setIsSearching);
	const setSearchQuery = useSearchStore(store => store.setSearchQuery);
	const setSearchResults = useSearchStore(store => store.setSearchResults);
	const abortControllerRef = useRef<AbortController | null>(null);

	function handleFocus(_event: FocusEvent<HTMLInputElement>) {
		const shouldShowSearchResults = Boolean(searchQuery && searchQuery.length > 0);
		onShowResults(shouldShowSearchResults);
		if(onInputFocus) onInputFocus()
	}

	function handleBlur() {
		if(onInputBlur) onInputBlur()
		// const shouldShowSearchResults = Boolean(searchQuery && searchQuery.length > 0);
		// if(onInputBlur && !shouldShowSearchResults) onInputBlur()
	}

	// Send request to internal search API (geocode locations and fuzzy search system names)
	async function fetchResults(query: string) {
		setIsSearching(true);
		// setSearchResults(null);
		abortControllerRef.current = new AbortController();
		const searchAbortSignal = abortControllerRef.current.signal;
		const newSearchResults = await getSearchResults(query, searchAbortSignal);
		setSearchResults(newSearchResults);
		setIsSearching(false);
	};

	// TODO: Consider adding debounce depending on method of search query
	function handleChange(event: ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) {
		const value = event.target.value;
		const params = new URLSearchParams(searchParams);
		// Abort previous fetch
		if(abortControllerRef.current) {
			abortControllerRef.current?.abort();
		}
		if(value) {
			params.set(SEARCH_PARAM_KEY, value);
		} else {
			params.delete(SEARCH_PARAM_KEY);
		}
		const shouldShowSearchResults = Boolean(value && value.length > 0);
		const shouldFetchSearchResults = Boolean(value && value.length >= SEARCH_MIN_QUERY);
		onShowResults(shouldShowSearchResults);
		if(shouldFetchSearchResults) {
			fetchResults(value);
		} else {
			setSearchResults(null);
		}
		setSearchQuery(value);
	};

	useEffect(() => {
		// Abort previous fetch
		return () => {
			if(abortControllerRef.current) {
				abortControllerRef.current?.abort();
			}
		}
	}, []);

	return (
		<form
			ref={ref}
			autoComplete="false"
			className={styles.SearchForm}
		>
			<Field
				id={"SearchFormField"}
				className={styles.SearchFormField}
				type="text"
				variant="underlined"
				placeholder={getLang("search", "placeholder")}
				labelText={getLang("search", "label")}
				hideLabelText={true}
				value={searchQuery}
				autoComplete="off"
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				ref={fieldRef}
			>
				<SvgIconSearch
					className={styles.SearchFormFieldIcon}
				/>
			</Field>
		</form>
	)
});

export default SearchForm;